import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { Badge, Button, EmptyState, Input, Panel, Select, Skeleton } from '../../components/ui';
import { ResourceDirectory } from './ResourceDirectory';
import { SharedAssetLibrary } from './SharedAssetLibrary';
import { PageHeader } from '../../layouts/Shells';
import { useProjects } from '../projects/hooks';
import { useFileBrowser } from './hooks';
import { FILE_CATEGORY_LABELS, formatFileSize } from './types';
import type { SelectOption } from '../../components/ui';
import type { FileBrowserFilters, FileBrowserItem, FileCategory } from './types';

/**
 * The Files page (handoff 9 navigation): every latest file version the
 * signed-in person can see, across all their projects, newest first. A
 * read-only browser — uploading, versioning and deleting stay in the task
 * panel, where the file's context lives. Filtering and pagination happen in
 * the database (handoff 39), and Row Level Security alone decides
 * visibility.
 */

const CATEGORY_FILTER_OPTIONS: SelectOption[] = [
  { value: 'all', label: 'All categories' },
  ...(Object.entries(FILE_CATEGORY_LABELS) as [FileCategory, string][])
    .filter(([key]) => key !== 'supplier_review') // arrives with Phase 6
    .map(([value, label]) => ({ value, label })),
];

/** Search waits for the person to stop typing before asking the database. */
function useDebouncedValue(value: string, delayMs: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebounced(value);
    }, delayMs);
    return () => {
      clearTimeout(timer);
    };
  }, [value, delayMs]);
  return debounced;
}

function formatFileDate(value: string): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }
  return parsed.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function FileBrowserRow({ item }: { item: FileBrowserItem }) {
  const size = formatFileSize(item.size_bytes);
  const uploader = item.uploader_name === '' ? 'Someone' : item.uploader_name;

  return (
    <li className="file-browser-row">
      <div className="file-row-main">
        <a className="file-name" href={item.url} target="_blank" rel="noreferrer">
          {item.name}
        </a>
        <span className="file-meta">
          {item.version_number > 1 ? `V${String(item.version_number)} · ` : ''}
          {FILE_CATEGORY_LABELS[item.category]}
          {size === '' ? '' : ` · ${size}`}
          {` · ${uploader}`}
          {` · ${formatFileDate(item.created_at)}`}
        </span>
        {item.review_status === 'approved' ? <Badge tone="success">Approved</Badge> : null}
      </div>
      <p className="file-browser-place">
        {item.project_name === '' ? (
          <span>No longer available to you</span>
        ) : (
          <Link
            className="file-browser-project"
            to={
              item.task_id === null
                ? `/projects/${encodeURIComponent(item.project_id)}`
                : `/projects/${encodeURIComponent(item.project_id)}?task=${encodeURIComponent(item.task_id)}`
            }
          >
            {item.project_name}
          </Link>
        )}
        {item.task_title === '' ? null : <span>{` · ${item.task_title}`}</span>}
      </p>
    </li>
  );
}

/** The attachment feed that used to be the whole page. */
function FileBrowser() {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<FileBrowserFilters['category']>('all');
  const [projectId, setProjectId] = useState<string>('all');

  const debouncedSearch = useDebouncedValue(search, 300);
  const filters: FileBrowserFilters = { search: debouncedSearch, category, projectId };

  const browser = useFileBrowser(filters);
  const projects = useProjects();

  const projectOptions: SelectOption[] = [
    { value: 'all', label: 'All projects' },
    ...(projects.data ?? []).map((project) => ({ value: project.id, label: project.name })),
  ];

  // Offset pages can drift while new files arrive; a row never shows twice.
  const seen = new Set<string>();
  const items = (browser.data?.pages ?? []).flat().filter((item) => {
    if (seen.has(item.id)) {
      return false;
    }
    seen.add(item.id);
    return true;
  });

  const filtered = search.trim() !== '' || category !== 'all' || projectId !== 'all';

  function body() {
    if (browser.isPending) {
      return <Skeleton lines={3} label="Loading files" />;
    }
    if (browser.isError) {
      return (
        <EmptyState
          title="Files could not be loaded"
          description="Something went wrong. Try again in a moment."
        />
      );
    }
    if (items.length === 0) {
      return filtered ? (
        <EmptyState title="Nothing matches" description="Try a different search or filter." />
      ) : (
        <EmptyState
          title="No files yet"
          description="Files attached to tasks appear here as they are uploaded."
        />
      );
    }
    return (
      <>
        <ul className="file-browser-list">
          {items.map((item) => (
            <FileBrowserRow key={item.id} item={item} />
          ))}
        </ul>
        {browser.hasNextPage ? (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              void browser.fetchNextPage();
            }}
            disabled={browser.isFetchingNextPage}
          >
            {browser.isFetchingNextPage ? 'Loading…' : 'Load more'}
          </Button>
        ) : null}
      </>
    );
  }

  const shown =
    browser.isPending || browser.isError || items.length === 0
      ? null
      : browser.hasNextPage
        ? `Showing ${String(items.length)}+ files`
        : `Showing ${String(items.length)} ${items.length === 1 ? 'file' : 'files'}`;

  return (
    <>
      <Panel
        title="Task & project attachments"
        actions={shown === null ? undefined : <span className="file-browser-count">{shown}</span>}
      >
        <div className="file-browser-controls">
          <Input
            label="Search"
            value={search}
            placeholder="Search file names"
            onChange={(event) => {
              setSearch(event.target.value);
            }}
          />
          <Select
            label="Category"
            options={CATEGORY_FILTER_OPTIONS}
            value={category}
            onChange={(event) => {
              setCategory(event.target.value as FileBrowserFilters['category']);
            }}
          />
          <Select
            label="Project"
            options={projectOptions}
            value={projectId}
            onChange={(event) => {
              setProjectId(event.target.value);
            }}
          />
        </div>

        {body()}
      </Panel>
    </>
  );
}

/** The three rooms of /files. Resource Directory lands first, deliberately. */
type FilesTab = 'resources' | 'attachments' | 'assets';

const TAB_LABELS: Record<FilesTab, string> = {
  resources: '🔗 Resource Directory',
  attachments: '📎 Task & Project Attachments',
  assets: '📁 Shared Asset Library',
};

/**
 * /files, as three tabs rather than one list.
 *
 * The attachment feed was the whole page and is now one tab of it. It is the
 * raw output of everybody's uploads, which makes it a poor landing view: the
 * thing people actually come here for is the handful of links they open every
 * day, so the directory is the default and the feed is one click away.
 *
 * The tab strip reuses `tracker-tab-nav` and `tracker-tab-button` on purpose.
 * src/styles/tabClasses.test.ts scans every component for those class names
 * and fails the build if one is missing from components.css, so borrowing them
 * inherits that guard instead of starting a third tab system.
 *
 * (That scanner matches the bare prefix too, so writing it without a suffix
 * anywhere in this file — even in a sentence like this one — would report an
 * undefined class. It found this comment on the first run.)
 */
export function FilesPage() {
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState<FilesTab>(() => {
    const requested = searchParams.get('tab');
    return requested !== null && Object.hasOwn(TAB_LABELS, requested)
      ? (requested as FilesTab)
      : 'resources';
  });

  return (
    <>
      {/* The description stays: it names what is in the three tabs below. */}
      <PageHeader
        title="Files"
        topbar
        description="Team links, task attachments and shared assets."
      />
      <nav className="tracker-tab-nav">
        {(Object.keys(TAB_LABELS) as FilesTab[]).map((key) => (
          <button
            key={key}
            type="button"
            className={`tracker-tab-button${tab === key ? ' tracker-tab-button--active' : ''}`}
            onClick={() => setTab(key)}
          >
            {TAB_LABELS[key]}
          </button>
        ))}
      </nav>

      {tab === 'resources' ? <ResourceDirectory /> : null}
      {tab === 'attachments' ? <FileBrowser /> : null}
      {tab === 'assets' ? (
        <Panel title="Shared asset library">
          <SharedAssetLibrary />
        </Panel>
      ) : null}
    </>
  );
}
